import { GlobalBus } from '@core/Bus/EventBus';
import type { IAudio } from './IAudio';
import {
  persistMusicMuted,
  persistSfxMuted,
  sanitizeAudioStorage,
} from './audioStorage';

let activeAudio: IAudio | null = null;
let busInitialized = false;

function applySfxEnabled(audio: IAudio, enabled: boolean): void {
  if (enabled) {
    // Menu Sound ON is a real user gesture — unlock SFX/Music autoplay policy.
    audio.noteUserGesture();
    if (audio.isSfxMuted()) {
      audio.unmuteSfx();
    }
  } else if (!audio.isSfxMuted()) {
    audio.muteSfx();
  }
}

function applyMusicEnabled(audio: IAudio, enabled: boolean): void {
  if (enabled) {
    audio.noteUserGesture();
    if (audio.isMusicMuted()) {
      audio.unmuteMusic();
    }
  } else if (!audio.isMusicMuted()) {
    audio.muteMusic();
  }
}

function emitSfxState(): void {
  // Always report persisted preference so temporary runtime mutes cannot desync Menu.
  GlobalBus.emit('audio:state:changed', {
    isMuted: sanitizeAudioStorage().sfxMuted,
  });
}

function emitMusicState(): void {
  // Menu Music toggle must reflect persisted preference, never a temporary
  // transport pause that flipped runtime mute without writing localStorage.
  GlobalBus.emit('audio:music:state:changed', {
    isMuted: sanitizeAudioStorage().musicMuted,
  });
}

function ensureBusListeners(): void {
  if (busInitialized) {
    return;
  }
  busInitialized = true;

  GlobalBus.on('audio:set:sound', (payload?: { enabled?: boolean }) => {
    if (payload?.enabled === undefined) {
      return;
    }
    persistSfxMuted(!payload.enabled);
    if (activeAudio) {
      applySfxEnabled(activeAudio, payload.enabled);
    }
    emitSfxState();
  });

  GlobalBus.on('audio:set:music', (payload?: { enabled?: boolean }) => {
    if (payload?.enabled === undefined) {
      return;
    }
    persistMusicMuted(!payload.enabled);
    if (activeAudio) {
      applyMusicEnabled(activeAudio, payload.enabled);
    }
    emitMusicState();
  });

  GlobalBus.on('audio:toggle:sound', () => {
    const nextEnabled = sanitizeAudioStorage().sfxMuted;
    persistSfxMuted(!nextEnabled);
    if (activeAudio) {
      applySfxEnabled(activeAudio, nextEnabled);
    }
    emitSfxState();
  });

  GlobalBus.on('audio:toggle:music', () => {
    // Toggle from persisted preference — never from runtime mute (reconnect may
    // have paused playback without changing localStorage).
    const nextEnabled = sanitizeAudioStorage().musicMuted;
    persistMusicMuted(!nextEnabled);
    if (activeAudio) {
      applyMusicEnabled(activeAudio, nextEnabled);
    }
    emitMusicState();
  });

  GlobalBus.on('audio:get:state', () => {
    emitSfxState();
  });

  GlobalBus.on('audio:get:music:state', () => {
    emitMusicState();
  });
}

export function registerActiveAudio(audio: IAudio): void {
  ensureBusListeners();
  activeAudio = audio;
  emitSfxState();
  emitMusicState();
}

export function unregisterActiveAudio(audio: IAudio): void {
  if (activeAudio === audio) {
    activeAudio = null;
  }
}

/**
 * Align runtime mute flags to localStorage without treating this as a user gesture.
 * Clears stale `muteMusic()` from older reconnect paths.
 */
export function syncRuntimeMuteToPreference(
  audio: IAudio | null = activeAudio
): void {
  if (!audio) {
    emitSfxState();
    emitMusicState();
    return;
  }
  const { sfxMuted, musicMuted } = sanitizeAudioStorage();
  if (sfxMuted && !audio.isSfxMuted()) {
    audio.muteSfx();
  } else if (!sfxMuted && audio.isSfxMuted()) {
    audio.unmuteSfx();
  }
  if (musicMuted && !audio.isMusicMuted()) {
    audio.muteMusic();
  } else if (!musicMuted && audio.isMusicMuted()) {
    audio.unmuteMusic();
  }
  emitSfxState();
  emitMusicState();
}

/**
 * Pause BGM for transport loss without changing the Music preference or Menu UI.
 * Call {@link restoreMusicAfterTransport} when the socket is healthy again.
 */
export function pauseMusicForTransport(
  audio: IAudio | null = activeAudio
): void {
  if (!audio || audio.isMusicMuted()) {
    return;
  }
  audio.stopMusic();
}

/** Resume BGM after reconnect when the persisted Music preference is ON. */
export function restoreMusicAfterTransport(
  audio: IAudio | null = activeAudio
): void {
  if (!audio) {
    emitMusicState();
    return;
  }
  const { musicMuted } = sanitizeAudioStorage();
  if (musicMuted) {
    if (!audio.isMusicMuted()) {
      audio.muteMusic();
    }
    emitMusicState();
    return;
  }
  // Preference ON: clear any stale runtime mute from older reconnect code paths.
  if (audio.isMusicMuted()) {
    audio.unmuteMusic();
  } else if (!audio.isMusicPlaying()) {
    audio.playMusic('bgMusic');
  }
  emitMusicState();
}

ensureBusListeners();
